import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

const PROTECTED_PREFIXES = ['/api/app/', '/api/admin/'];

// Only attach the bearer token to same-origin requests whose pathname starts
// with one of our protected prefixes. The earlier `url.includes(prefix)` check
// would have leaked the JWT to any URL that happened to contain "/api/app/"
// anywhere in its path or query string — e.g. a third-party callback URL.
function needsAuth(url: string): boolean {
  let path: string;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    const parsed = new URL(url);
    if (parsed.origin !== window.location.origin) return false;
    path = parsed.pathname;
  } else {
    path = url;
  }
  return PROTECTED_PREFIXES.some((p) => path.startsWith(p));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!needsAuth(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);

  return from(auth.getToken()).pipe(
    switchMap((token) => {
      const authedReq = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;

      return next(authedReq).pipe(
        catchError((err) => {
          if (err?.status !== 401 || !token) {
            return throwError(() => err);
          }
          // Token may be stale; drop cache and retry once with a fresh one.
          auth.invalidateToken();
          return from(auth.getToken()).pipe(
            switchMap((freshToken) => {
              if (!freshToken) return throwError(() => err);
              const retried = req.clone({
                setHeaders: { Authorization: `Bearer ${freshToken}` },
              });
              return next(retried);
            }),
          );
        }),
      );
    }),
  );
};
